import { Logger } from 'winston';
import { DevLakeDatabase } from '../db/connection';

export interface DeploymentFrequency {
  period: string;
  deploymentCount: number;
}

export interface LeadTimeForChanges {
  date: string;
  leadTime: number;
}

export interface MeanTimeToRestore {
  date: string;
  mttr: number;
}

export interface ChangeFailureRate {
  date: string;
  rate: number;
  failureRate?: number; // Added for compatibility with different naming conventions
}

export interface DoraMetricsSummary {
  deploymentFrequency: {
    value: string;
    level: 'elite' | 'high' | 'medium' | 'low';
  };
  leadTimeForChanges: {
    value: string;
    level: 'elite' | 'high' | 'medium' | 'low';
  };
  meanTimeToRestore: {
    value: string;
    level: 'elite' | 'high' | 'medium' | 'low';
  };
  changeFailureRate: {
    value: string;
    level: 'elite' | 'high' | 'medium' | 'low';
  };
}

export class DoraMetricsService {
  // Store metadata for summary endpoint
  private deploymentFrequencyMetadata: {
    score: string;
    value: string;
    trend: string;
  };
  
  private leadTimeMetadata: {
    score: string;
    value: string;
    trend: string;
  };
  
  private mttrMetadata: {
    score: string;
    value: string;
    trend: string;
  };
  
  private failureRateMetadata: {
    score: string;
    value: string;
    trend: string;
  };

  constructor(
    private readonly database: DevLakeDatabase,
    private readonly logger: Logger,
  ) {
    // Initialize metadata with default values
    this.deploymentFrequencyMetadata = {
      score: 'Elite',
      value: 'Multiple deploys per day',
      trend: 'improving'
    };
    
    this.leadTimeMetadata = {
      score: 'Elite',
      value: '< 1 day',
      trend: 'stable'
    };
    
    this.mttrMetadata = {
      score: 'Elite',
      value: '< 1 hour',
      trend: 'improving'
    };
    
    this.failureRateMetadata = {
      score: 'High',
      value: '0-15%',
      trend: 'stable'
    };
  }

  /**
   * Get a list of all projects available in DevLake
   */
  async getProjects(): Promise<string[]> {
    try {
      this.logger.info('Fetching projects from DevLake');
      
      // Return mock projects data similar to test-backend.js
      return [
        'elite-project-cicd',
        'high-project-cicd',
        'medium-project-cicd',
        'low-project-cicd'
      ];
    } catch (error) {
      this.logger.error('Error fetching projects:', error);
      throw error;
    }
  }

  /**
   * Get deployment frequency data for a project
   */
  async getDeploymentFrequency(
    projectKey: string,
    timeRange: string = '30d'
  ): Promise<DeploymentFrequency[]> {
    try {
      this.logger.info(`Fetching deployment frequency for project ${projectKey} with timeRange ${timeRange}`);
      
      // Generate mock data similar to test-backend.js
      const today = new Date();
      
      // Return an array directly as expected by the frontend
      const data = Array.from({ length: 30 }, (_, i) => {
        const date = new Date(today);
        date.setDate(date.getDate() - (29 - i));
        return {
          period: date.toISOString().split('T')[0],
          deploymentCount: Math.floor(Math.random() * 5) + 1 // 1-6 deploys per day
        };
      });
      
      // Update metadata for summary endpoint
      this.deploymentFrequencyMetadata = {
        score: 'Elite',
        value: 'Multiple deploys per day',
        trend: 'improving'
      };
      
      return data;
    } catch (error) {
      this.logger.error(`Error fetching deployment frequency for project ${projectKey}:`, error);
      throw error;
    }
  }

  /**
   * Get lead time for changes data for a project
   */
  async getLeadTimeForChanges(
    projectKey: string,
    timeRange: string = '30d'
  ): Promise<LeadTimeForChanges[]> {
    try {
      this.logger.info(`Fetching lead time for changes for project ${projectKey} with timeRange ${timeRange}`);
      
      // Generate mock data similar to test-backend.js
      const today = new Date();
      
      // Return an array directly as expected by the frontend
      const data = Array.from({ length: 30 }, (_, i) => {
        const date = new Date(today);
        date.setDate(date.getDate() - (29 - i));
        return {
          date: date.toISOString().split('T')[0],
          leadTime: Math.floor(Math.random() * 20) + 1 // 1-21 hours
        };
      });
      
      // Update metadata for summary endpoint
      this.leadTimeMetadata = {
        score: 'Elite',
        value: '< 1 day',
        trend: 'stable'
      };
      
      return data;
    } catch (error) {
      this.logger.error(`Error fetching lead time for changes for project ${projectKey}:`, error);
      throw error;
    }
  }

  /**
   * Get mean time to restore data for a project
   */
  async getMeanTimeToRestore(
    projectKey: string,
    timeRange: string = '30d'
  ): Promise<MeanTimeToRestore[]> {
    try {
      this.logger.info(`Fetching MTTR for project ${projectKey} with timeRange ${timeRange}`);
      
      // Generate mock data similar to test-backend.js
      const today = new Date();
      
      // Return an array directly as expected by the frontend
      const data = Array.from({ length: 30 }, (_, i) => {
        const date = new Date(today);
        date.setDate(date.getDate() - (29 - i));
        return {
          date: date.toISOString().split('T')[0],
          mttr: Math.floor(Math.random() * 50) + 10 // 10-60 minutes (converted to hours)
        };
      });
      
      // Update metadata for summary endpoint
      this.mttrMetadata = {
        score: 'Elite',
        value: '< 1 hour',
        trend: 'improving'
      };
      
      return data;
    } catch (error) {
      this.logger.error(`Error fetching MTTR for project ${projectKey}:`, error);
      throw error;
    }
  }

  /**
   * Get change failure rate data for a project
   */
  async getChangeFailureRate(
    projectKey: string,
    timeRange: string = '30d'
  ): Promise<ChangeFailureRate[]> {
    try {
      this.logger.info(`Fetching change failure rate for project ${projectKey} with timeRange ${timeRange}`);
      
      // Generate mock data similar to test-backend.js
      const today = new Date();
      
      // Return an array directly as expected by the frontend
      const data = Array.from({ length: 30 }, (_, i) => {
        const date = new Date(today);
        date.setDate(date.getDate() - (29 - i));
        return {
          date: date.toISOString().split('T')[0],
          rate: Math.floor(Math.random() * 15) // 0-15% failure rate
        };
      });
      
      // Update metadata for summary endpoint
      this.failureRateMetadata = {
        score: 'High',
        value: '0-15%',
        trend: 'stable'
      };
      
      return data;
    } catch (error) {
      this.logger.error(`Error fetching change failure rate for project ${projectKey}:`, error);
      throw error;
    }
  }

  /**
   * Get summary of all DORA metrics for a project
   */
  async getDoraMetricsSummary(
    projectKey: string,
    timeRange: string = '30d'
  ): Promise<DoraMetricsSummary> {
    try {
      this.logger.info(`Fetching DORA metrics summary for project ${projectKey} with timeRange ${timeRange}`);
      
      // Return mock summary data similar to test-backend.js
      return {
        deploymentFrequency: {
          value: 'Multiple deploys per day',
          level: 'elite'
        },
        leadTimeForChanges: {
          value: '24 hours',
          level: 'elite'
        },
        meanTimeToRestore: {
          value: '1 hour',
          level: 'elite'
        },
        changeFailureRate: {
          value: '5%',
          level: 'elite'
        }
      };
    } catch (error) {
      this.logger.error(`Error fetching DORA metrics summary for project ${projectKey}:`, error);
      throw error;
    }
  }
}
